// The VERA composer: a roomy place to work with VERA.
// Type, paste or drop files and images, point VERA at tools (or leave it on Auto), use / commands, or talk with voice.

import { useEffect, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from "react";
import { Icon, Orb, useStudio } from "./ui";
import { vera, type Attachment } from "../api/vera";
import { dictate } from "../lib/speech";
import { COMMANDS, type ToolId } from "../lib/tools";
import { fmtBytes, MAX_FILES, readFiles, pastedFiles } from "../lib/attachments";
import { desktop } from "../lib/desktop";
import { L, currentLang } from "../lib/i18n";

export type ComposerProps = {
  placeholder?: string;
  text: string;
  setText: (t: string) => void;
  attachments: Attachment[];
  setAttachments: (f: (a: Attachment[]) => Attachment[]) => void;
  tools: ToolId[];
  setTools: (t: ToolId[]) => void;
  busy: boolean;
  onSend: () => void;
  onStop?: () => void;
  onVoice: () => void;
};

export function Composer({ placeholder, text, setText, attachments, setAttachments, tools, setTools, busy, onSend, onVoice }: ComposerProps) {
  const { toast, go, getToken, consent, openConsent } = useStudio();
  const [dictating, setDictating] = useState(false);
  const stopDictation = useRef<(() => void) | null>(null);
  const textRef = useRef(text);
  textRef.current = text;
  useEffect(() => () => stopDictation.current?.(), []);
  async function toggleDictation() {
    if (dictating) { stopDictation.current?.(); stopDictation.current = null; setDictating(false); return; }
    if (!consent.mic) { toast(L("Turn on the microphone in Privacy & consent first.", "Aktivizo më parë mikrofonin te Privatësia dhe pëlqimi.")); openConsent(); return; }
    if (vera.mode !== "live") { toast(L("Dictation works with live VERA.", "Diktimi funksionon me VERA-n e vërtetë.")); return; }
    try {
      setDictating(true);
      stopDictation.current = await dictate(getToken,
        (said) => { const t = textRef.current; const next = t.trim() ? `${t.trimEnd()} ${said}` : said; textRef.current = next; setText(next); },
        (msg) => { toast(msg); setDictating(false); stopDictation.current = null; });
    } catch (e) { setDictating(false); toast(e instanceof Error ? e.message : L("Dictation couldn't start.", "Diktimi nuk u nis.")); }
  }
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [menu, setMenu] = useState(false);
  const [cmdIndex, setCmdIndex] = useState(0);

  // Grow with the text, up to a comfortable height.
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 260)}px`;
  }, [text]);

  const slash = /^\/(\w*)$/.exec(text.trim()) && !text.includes(" ");
  const matches = useMemo(() => (slash ? COMMANDS.filter((c) => c.cmd.startsWith(text.trim().toLowerCase())) : []), [slash, text]);
  useEffect(() => setCmdIndex(0), [matches.length]);

  function runCommand(c: (typeof COMMANDS)[number]) {
    if (c.tool && !tools.includes(c.tool)) setTools([...tools, c.tool]);
    setText((currentLang() === "sq" ? c.insertSq : c.insert) ?? "");
    requestAnimationFrame(() => { const ta = taRef.current; if (ta) { ta.focus(); ta.setSelectionRange(ta.value.length, ta.value.length); } });
  }

  async function addFiles(list: FileList | File[]) {
    if (!list.length) return;
    const r = await readFiles(list, attachments.length);
    if (r.files.length) setAttachments((a) => [...a, ...r.files]);
    if (r.skipped.length) toast(L(`Not added: ${r.skipped.join("; ")}`, `Nuk u shtuan: ${r.skipped.join("; ")}`));
  }

  function onPaste(e: ClipboardEvent<HTMLTextAreaElement>) {
    const files = pastedFiles(e.clipboardData);
    if (files.length) { e.preventDefault(); void addFiles(files); }
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (matches.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setCmdIndex((i) => (i + 1) % matches.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setCmdIndex((i) => (i - 1 + matches.length) % matches.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); runCommand(matches[cmdIndex]); return; }
      if (e.key === "Escape") { setText(""); return; }
    }
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); onSend(); }
  }

  const canSend = (text.trim().length > 0 || attachments.length > 0) && !busy;

  return (
    <div className="composer2">
      {!!matches.length && (
        <div className="cmdmenu" role="listbox" aria-label={L("Commands", "Komandat")}>
          {matches.map((c, i) => (
            <button key={c.cmd} role="option" aria-selected={i === cmdIndex} className={i === cmdIndex ? "on" : ""} onMouseEnter={() => setCmdIndex(i)} onClick={() => runCommand(c)}>
              <code>{c.cmd}</code><b>{L(c.label, c.labelSq)}</b><span>{L(c.detail, c.detailSq)}</span>
            </button>))}
        </div>
      )}

      {!!attachments.length && (
        <div className="tray" aria-label={L("Attached", "Të bashkëngjitura")}>
          {attachments.map((a, i) => (
            <div className={`tfile ${a.kind === "image" ? "timg" : ""}`} key={a.name + i} title={a.name}>
              {a.kind === "image" && a.thumb ? <img src={a.thumb} alt={a.name} /> : <span className="ticon"><Icon name={a.kind === "document" ? "docai" : "doc"} size={18} /></span>}
              <span className="tmeta"><b>{a.name}</b><small>{a.kind === "image" ? `${L("Image", "Imazh")}${a.width ? ` · ${a.width}×${a.height}` : ""}` : a.kind === "document" ? L("Document", "Dokument") : L("Text", "Tekst")}{a.size ? ` · ${fmtBytes(a.size)}` : ""}</small></span>
              <button className="tx" aria-label={L(`Remove ${a.name}`, `Hiqe ${a.name}`)} onClick={() => setAttachments((x) => x.filter((_, j) => j !== i))}><Icon name="x" size={12} /></button>
            </div>))}
        </div>
      )}

      <label htmlFor="say" className="sr">{L("Message VERA", "Mesazh për VERA-n")}</label>
      <textarea id="say" ref={taRef} rows={2} value={text} placeholder={placeholder ?? L("Ask VERA anything, or drop files here", "Pyete VERA-n për çdo gjë, ose lësho skedarë këtu")}
        onChange={(e) => setText(e.target.value)} onKeyDown={onKey} onPaste={onPaste} />

      <div className="cbar">
          <div className="pluswrap">
            <button type="button" className="iconbtn" aria-label={L("Add files and more", "Shto skedarë dhe më shumë")} aria-expanded={menu} onClick={() => setMenu((m) => !m)}><Icon name="plus" /></button>
            {menu && (
              <div className="plusmenu" onMouseLeave={() => setMenu(false)}>
                <button onClick={() => { setMenu(false); fileRef.current?.click(); }}><Icon name="clip" size={16} /> {L("Upload files or images", "Ngarko skedarë ose imazhe")}</button>
                {desktop && <button onClick={() => { setMenu(false); go("files"); }}><Icon name="folder" size={16} /> {L("From folders on this computer", "Nga dosjet në këtë kompjuter")}</button>}
                <button onClick={() => { setMenu(false); setText("/"); taRef.current?.focus(); }}><Icon name="sparkle" size={16} /> {L("Commands", "Komandat")}</button>
                <p className="faint">{L("You can also paste a screenshot (Ctrl+V) or drop files anywhere in the conversation. Up to", "Mund të ngjitësh edhe një pamje ekrani (Ctrl+V) ose të lëshosh skedarë kudo në bisedë. Deri në")} {MAX_FILES} {L("at a time.", "njëherësh.")}</p>
              </div>
            )}
          </div>
          <input ref={fileRef} type="file" multiple hidden onChange={(e) => { if (e.target.files) void addFiles(e.target.files); e.target.value = ""; }} />
          <div className="toolchips" aria-hidden="true" />
        <div className="cright">
          <button type="button" className={`iconbtn ${dictating ? "on" : ""}`} aria-pressed={dictating} aria-label={dictating ? L("Stop dictation", "Ndalo diktimin") : L("Dictate", "Dikto")} title={dictating ? L("Stop dictation", "Ndalo diktimin") : L("Dictate (English, German or Albanian)", "Dikto (shqip, anglisht ose gjermanisht)")} onClick={() => void toggleDictation()}><Icon name={dictating ? "stop" : "mic"} /></button>
          <button type="button" className="iconbtn" aria-label={L("Talk with VERA", "Fol me VERA-n")} title={L("Talk with VERA", "Fol me VERA-n")} onClick={onVoice}><Icon name="wave" /></button>
          <button type="button" className="iconbtn send" aria-label={L("Send", "Dërgo")} disabled={!canSend} onClick={onSend}>{busy ? <Orb size={16} mode="think" /> : <Icon name="send" />}</button>
        </div>
      </div>
    </div>
  );
}
